import { parameterizeUrl } from "$lib/core";
import { createOption, FormRestructureEvent } from "../core/";

/**
 * @param {HTMLElement} element The form item for which items should be loaded.
 * @param {import("../../public").SelectListItem[]} data
 * @param {"checkboxes" | "select" | "autocomplete" | string} [type=""] The type of options to load. If no value is given, it will be derived from the `data-field-name` attribute on `element`.
 * @returns {void}
 */
export function loadOptions(element, data, type = "") {
  if (!type) {
    const field = /** @type {HTMLElement?} */ (element.closest(":is(.field,.fieldgroup)"));
    if (field) {
      type = field.dataset.fieldType ?? "";
    }
  }
  switch (type) {
    case "checkboxes":
      dataLoadCheckboxes(element, data);
      break;
    case "select":
      dataLoadSelect(element, data);
      break;
    case "autocomplete":
      break;
    default:
      break;
  }
}

/**
 * @param {HTMLElement} element The actual element with a `name` attribute and `value` property.
 * @param {string|URL|Request} url The endpoint to contact.
 * @param {import("./public").DataLoadRequestInit|undefined} [init=undefined]
 */
export async function fetchField(element, url, init = undefined) {

  init = init ?? {};
  if (init.args != null && init.body == null && init.method === "post") {
    init.body = JSON.stringify(init.args);
  }

  let jsPathString = init.jsPath ?? element.dataset.loadPath ?? "";
  const jsPath = jsPathString ? jsPathString.split(".") : [];

  try {
    const response = await fetch(url, init);
    /** @type {Record<string, any>} */
    const result = await response.json();
    let value = result;
    for (let key of jsPath) {
      value = value[key];
    }

    if (Array.isArray(value)) {
      loadOptions(element, /** @type {import("../../public").SelectListItem[]} */ (value), init.fieldType);
    } else if ("value" in element) {
      element.value = value;
    }

  } catch (err) {
      console.error(err);
  }
}

/**
 * @param {HTMLFormElement} form 
 */
export function addDataLoading(form) {
  form.addEventListener("restructure", async event => {
		if (!(event instanceof FormRestructureEvent)) return;

		for (const entry of event.added) {
			if (!entry.field) continue;

      const fieldType = entry.field.dataset.fieldType;
      if (!fieldType) continue;

      for (const node of entry.field.querySelectorAll(":scope [data-load-url]")) {
        const element = /** @type {HTMLElement} */ (node);

        const dataUrl = element.dataset.loadUrl;
        if (!dataUrl) continue;

        // [data-load-watch="fruits,drinks"] => anytime `:is([name="fruits"],[name="drinks"])` emits a `change` event, reload.
        const targetNames = element.dataset.loadWatch;
        if (!targetNames) continue;
        const anyOfNamesSelector = targetNames.split(",").map(name => `[name="${name}"]`).join(",");

        // [data-load-method="post"] => use a "post" submission instead.
        const method = element.dataset.loadMethod ?? "get";

        // [data-load-path="value"] => assign value / generate options using the "value" key in the JSON response.
        const jsPath = element.dataset.loadPath ?? "value";

        for (const watchedNode of form.querySelectorAll(`:scope :is(${anyOfNamesSelector})`)) {
          const watchedElement = /** @type {HTMLElement} */ (watchedNode);
          watchedElement.addEventListener("change", async () => {
            const formData = new FormData(form);
            const { url, args } = parameterizeUrl(dataUrl, formData);
            await fetchField(element, url, {
              method,
              jsPath,
              args,
              fieldType,
            });
          });
        }
      }
    }
  })
}

/**
 * @param {HTMLElement} element
 * @param {import("../../public").SelectListItem[]} data
 */
function dataLoadCheckboxes(element, data) {
  const field = /** @type {HTMLElement?} */ (element?.closest(":is(.field,.fieldgroup)"));
  if (!field) {
    console.error("No field or fieldgroup found for element: ", element);
    return;
  }
  const selector = `.form-item:has(> input)`;
  const formItems = field.matches(selector) ? [field] : [...field.querySelectorAll(`:scope ${selector}`)];
  if (!element || !data) return;
  
  if (formItems.length > 0) {
    for (const item of formItems) {
      item.remove();
    }
  }
  
  let i = 0;
  const name = field.dataset.fieldName ?? "";
  for (const item of data) {
    const option = createOption("checkboxes", name, i++, item);
    element.append(option);
  }
}

/**
 * @param {HTMLElement} element
 * @param {import("../../public").SelectListItem[]} data
 */
function dataLoadSelect(element, data) {
  if (!(element instanceof HTMLSelectElement) || !data) return;
  
  const list = /** @type {{text: string, value: string}[]} */ (data);
  element.innerHTML = window.BU.policy.createHTML("");
  
  if (element.dataset.noEmpty !== "true") {
    if (list.every(item => !!item.value)) {
      const emptyLabel = element.dataset.emptyLabel ? element.dataset.emptyLabel : "Choose...";
      list.unshift({ text: emptyLabel, value: "" });
    }
  }
  
  let i = 0;
  for (const item of list) {
    const option = createOption("select", "", i++, item);
    element.append(option);
  }
}
